"use client";

import { useEffect, useState } from "react";

/**
 * Hook reactivo + helpers para conocer el estado real de red.
 *
 * - `online`: combinación de `navigator.onLine` Y un ping real al backend.
 *   navigator.onLine miente con datos móviles intermitentes en zona rural,
 *   por eso reforzamos con un HEAD a `/api/health` (timeout 3s) cada 30s
 *   mientras la pestaña esté visible.
 * - `since`: timestamp ms del último cambio de estado.
 * - `reachable`: resultado del último probe (independiente de navigator).
 *
 * El probe lo arranca el `DrainerBoot` global (via `startNetworkProbeLoop`)
 * para que `isOnline()` sea confiable aunque no haya ningún hook montado.
 */
export type NetworkStatus = {
  online: boolean;
  since: number;
  reachable: boolean;
};

const PROBE_URL = "/api/health";
const PROBE_TIMEOUT_MS = 3_000;
const PROBE_INTERVAL_MS = 30_000;

// Estado global (compartido entre el hook y el helper imperativo `isOnline()`).
let _reachable = true;
// Conectividad de TRANSPORTE según @capacitor/network. null = desconocida (web
// antes del 1er getStatus, o plugin ausente). Cuando es boolean es la fuente de
// verdad PRIORITARIA: navigator.onLine MIENTE (se queda en false) en el WebView
// de Android/iOS tras reconectar, lo que dejaba el drainer sin disparar.
let _nativeConnected: boolean | null = null;
let _lastProbeAt = 0;
let _probeInFlight: Promise<boolean> | null = null;
let _loopStarted = false;
let _loopInterval: ReturnType<typeof setInterval> | null = null;

const reachListeners = new Set<() => void>();
function notifyReach() {
  reachListeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* listener no debe romper otros */
    }
  });
}

function navigatorOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine;
}

/** Transporte real: señal nativa de Capacitor si la conocemos; si no, navigator.onLine. */
function transportOnline(): boolean {
  if (_nativeConnected !== null) return _nativeConnected;
  return navigatorOnline();
}

/**
 * Hace un HEAD a /api/health con timeout. No reusa fetch cacheado (cache:
 * "no-store"). Devuelve true si el backend respondió OK dentro del timeout.
 *
 * Coalescencia: si ya hay un probe en vuelo, devuelve la misma promesa
 * (evita tormentas si varios callers piden a la vez).
 */
export async function probeReachability(): Promise<boolean> {
  if (typeof navigator === "undefined") return true;
  if (!transportOnline()) {
    const changed = _reachable !== false;
    _reachable = false;
    _lastProbeAt = Date.now();
    if (changed) notifyReach();
    return false;
  }
  if (_probeInFlight) return _probeInFlight;
  _probeInFlight = (async () => {
    const prev = _reachable;
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);
      const res = await fetch(PROBE_URL, {
        method: "HEAD",
        cache: "no-store",
        signal: ctrl.signal,
        // same-origin para incluir la cookie de sesión: aunque /api/health
        // está marcado como público en middleware, hay deployments con
        // edge functions cacheadas que pueden seguir requiriendo auth.
        credentials: "same-origin",
        // Evita que un SW intercepte y mienta sobre el resultado.
        headers: { "X-Probe": "1" },
      }).finally(() => clearTimeout(t));
      _reachable = res.ok;
    } catch {
      _reachable = false;
    } finally {
      _lastProbeAt = Date.now();
      _probeInFlight = null;
      if (_reachable !== prev) notifyReach();
    }
    return _reachable;
  })();
  return _probeInFlight;
}

/**
 * Arranca el loop global de probes. Idempotente: llamar varias veces no
 * crea múltiples intervals.
 *
 * Disparadores adicionales:
 *   - `online` del browser → probe inmediato (para confirmar).
 *   - `offline` del browser → marca reachable=false sin probe.
 *   - `visibilitychange` → al volver a visible, probe inmediato.
 */
export function startNetworkProbeLoop(): void {
  if (_loopStarted) return;
  if (typeof window === "undefined") return;
  _loopStarted = true;

  window.addEventListener("online", () => {
    void probeReachability();
  });
  window.addEventListener("offline", () => {
    const changed = _reachable !== false;
    _reachable = false;
    _lastProbeAt = Date.now();
    if (changed) notifyReach();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && transportOnline()) {
      void probeReachability();
    }
  });

  // @capacitor/network: conectividad NATIVA fiable (navigator.onLine miente en
  // el WebView de Android/iOS). Si el plugin está disponible, sus eventos
  // disparan/confirman el probe; si no (web sin el plugin), seguimos con
  // navigator + el HEAD a /api/health. Import dinámico → no rompe SSR ni si
  // el plugin no está instalado.
  const markOffline = () => {
    const changed = _reachable !== false;
    _reachable = false;
    _lastProbeAt = Date.now();
    if (changed) notifyReach();
  };
  import("@capacitor/network")
    .then(({ Network }) => {
      Network.addListener("networkStatusChange", (s) => {
        _nativeConnected = s.connected;
        if (!s.connected) markOffline();
        else void probeReachability();
      });
      void Network.getStatus().then((s) => {
        _nativeConnected = s.connected;
        if (!s.connected) markOffline();
        else void probeReachability();
      });
    })
    .catch(() => {
      /* plugin no disponible → modo navigator + probe */
    });

  // Primer probe al arrancar.
  void probeReachability();

  _loopInterval = setInterval(() => {
    if (typeof document === "undefined") return;
    if (document.visibilityState !== "visible") return;
    if (!transportOnline()) return;
    void probeReachability();
  }, PROBE_INTERVAL_MS);
}

export function stopNetworkProbeLoop(): void {
  if (_loopInterval) clearInterval(_loopInterval);
  _loopInterval = null;
  _loopStarted = false;
}

/** Suscripción a cambios en `reachable` (para hooks/listeners externos). */
export function subscribeReachability(fn: () => void): () => void {
  reachListeners.add(fn);
  return () => {
    reachListeners.delete(fn);
  };
}

/** Estado imperativo (sync) — combina navigator + reachable. */
export function isOnline(): boolean {
  return transportOnline() && _reachable;
}

/** Sólo el flag de probe, sin mezclar con navigator. */
export function isReachable(): boolean {
  return _reachable;
}

// Estado inicial DETERMINÍSTICO para SSR/hidratación: siempre `online: true`
// con `since: 0`. El useEffect post-mount actualiza con el valor real del
// navegador. Evita hydration mismatch (React error #418).
const INITIAL_STATUS: NetworkStatus = {
  online: true,
  since: 0,
  reachable: true,
};

export function useNetworkStatus(): NetworkStatus {
  const [status, setStatus] = useState<NetworkStatus>(INITIAL_STATUS);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const recompute = () => {
      setStatus({
        online: isOnline(),
        since: Date.now(),
        reachable: _reachable,
      });
    };

    // Sincroniza con el estado real después de montar.
    recompute();

    const goOnline = () => {
      recompute();
      void probeReachability().then(recompute);
    };
    const goOffline = () => {
      _reachable = false;
      _lastProbeAt = Date.now();
      recompute();
    };

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const unsubReach = subscribeReachability(recompute);

    // Asegura que el loop global esté corriendo (DrainerBoot ya lo hace,
    // pero es idempotente y útil si el hook se usa sin DrainerBoot).
    startNetworkProbeLoop();

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      unsubReach();
    };
  }, []);

  return status;
}
